import { DEXSCREENER_PRICE_URL, NATIVE_TO_WRAPPED_FOR_PRICE } from './constants';

const NATIVE_PLACEHOLDER = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
const MAX_TOKENS_PER_REQUEST = 50;

/**
 * Get the token address to use for DexScreener price request (wrapped address for native tokens).
 * @param {number|string} chainId
 * @param {string} address - Token contract address or native placeholder
 * @returns {string} Address to send in the API request
 */
export function getPriceLookupAddress(chainId, address) {
  if (!address) return address;
  const chain = Number(chainId);
  const addrLower = address.toLowerCase();
  if (addrLower === NATIVE_PLACEHOLDER.toLowerCase() && NATIVE_TO_WRAPPED_FOR_PRICE[chain]) {
    return NATIVE_TO_WRAPPED_FOR_PRICE[chain];
  }
  return address;
}

/**
 * Fetch USD prices for multiple tokens from the DexScreener price API.
 * @param {Array<{ chainId: number|string, address: string }>} tokenRequests - Max 50 per request; will be batched
 * @param {number} [minLiquidityUsd=10000]
 * @returns {Promise<{ priceMap: Record<string, number>, error?: string }>} priceMap key: `${chainId}-${address.toLowerCase()}`
 */
export async function fetchTokenPrices(tokenRequests, minLiquidityUsd = 10000) {
  const priceMap = {};
  if (!tokenRequests || tokenRequests.length === 0) {
    return { priceMap };
  }

  const batches = [];
  for (let i = 0; i < tokenRequests.length; i += MAX_TOKENS_PER_REQUEST) {
    batches.push(tokenRequests.slice(i, i + MAX_TOKENS_PER_REQUEST));
  }

  for (const batch of batches) {
    const tokens = batch.map(({ chainId, address }) => ({
      token: getPriceLookupAddress(chainId, address),
      chain: Number(chainId),
    }));

    let res;
    try {
      res = await fetch(DEXSCREENER_PRICE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokens, minLiquidityUsd }),
      });
    } catch (err) {
      console.error('DexScreener price fetch failed:', err);
      return { priceMap, error: err.message };
    }

    let data;
    try {
      data = await res.json();
    } catch {
      return { priceMap, error: 'Invalid response' };
    }

    if (!res.ok) {
      const msg = data?.error || data?.message || `HTTP ${res.status}`;
      return { priceMap, error: msg };
    }

    if (!data.success) {
      return { priceMap, error: data?.error || 'Invalid response' };
    }

    // Support array format: results[i] matches batch[i]
    if (Array.isArray(data.results)) {
      data.results.forEach((result, i) => {
        const req = batch[i];
        if (!req) return;
        const addrLower = req.address.toLowerCase();
        const key = `${req.chainId}-${addrLower}`;
        const price = result?.priceFound ? Number(result.priceUsd) : 0;
        const value = Number.isFinite(price) ? price : 0;
        priceMap[key] = value;
        // Native token: also store under wrapped key so lookups by either key find the same price
        if (addrLower === NATIVE_PLACEHOLDER.toLowerCase()) {
          const wrapped = NATIVE_TO_WRAPPED_FOR_PRICE[Number(req.chainId)];
          if (wrapped) {
            priceMap[`${req.chainId}-${wrapped.toLowerCase()}`] = value;
          }
        }
      });
    }

    // Support map format: backend returns priceMap/prices keyed by "chainId-address" (lowercase address)
    const mapPayload = data.priceMap || data.prices;
    if (mapPayload && typeof mapPayload === 'object' && !Array.isArray(mapPayload)) {
      Object.entries(mapPayload).forEach(([key, val]) => {
        const price = Number(val);
        if (Number.isFinite(price) && key) {
          const normalizedKey = key.toLowerCase().replace(/^(\d+)-0x/, (_, cid) => `${cid}-0x`);
          priceMap[normalizedKey] = price;
        }
      });
    }
  }

  return { priceMap };
}
